import React, { useEffect, useRef, useState } from 'react';
import { Camera, User, Smartphone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import DatePicker from '../ui/DatePicker';
import { RELATIONSHIPS } from '../../lib/constants';
import { useStoryStore } from '../../store/storyStore';
import { useFamilyGroupStore } from '../../store/familyGroupStore';
import { useFamilyInvitationStore } from '../../store/familyInvitationStore';
import { validateDateOfBirth } from '../../lib/utils';
import { useFilePreview } from '../../hooks/useFilePreview';
import { useNavigate } from 'react-router-dom';
import FamilySelector from './FamilySelector';
import { useOnboardingStore } from '../../store/onboardingStore';
import FrequencyPicker, {
  type NotificationFrequency,
} from '../notifications/FrequencyPicker';
import PlatformInstallGuide, {
  detectInstallPlatform,
  type InstallPlatform,
} from '../notifications/PlatformInstallGuide';
import {
  upsertPreferences,
  savePushSubscription,
} from '../../lib/notificationPreferences';
import { isStandalone, subscribeToPush } from '../../lib/pwaInstall';
import { supabase } from '../../lib/supabase';

type RecordingMode = 'presencial' | 'distancia';
type NotificationChannel = 'push' | 'email';
type TimeSlot = 'morning' | 'midday' | 'evening';
type Step = 1 | 2 | 3 | 4 | 5;

interface CreateStoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface TimeSlotOption {
  value: TimeSlot;
  label: string;
  hour: number;
  hourLabel: string;
}

const TIME_SLOTS: TimeSlotOption[] = [
  { value: 'morning', label: 'Mañana', hour: 9, hourLabel: '9:00' },
  { value: 'midday', label: 'Mediodía', hour: 13, hourLabel: '13:00' },
  { value: 'evening', label: 'Tarde', hour: 19, hourLabel: '19:00' },
];

const slotToHour = (slot: TimeSlot): number =>
  TIME_SLOTS.find((s) => s.value === slot)!.hour;

const STEP_TITLES: Record<Step, string> = {
  1: 'Sobre la persona',
  2: 'Cómo vas a grabar',
  3: 'Frecuencia de los avisos',
  4: 'Hora del día',
  5: 'Cómo recibirás los avisos',
};

const CreateStoryModal: React.FC<CreateStoryModalProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState<Step>(1);
  const [formData, setFormData] = useState({
    relationship: '',
    dateOfBirth: '',
  });
  // Protagonist's name. Title is derived from these (auto-built as
  // "La historia de [firstName] [lastName1] [lastName2]") so the
  // inviter doesn't have to think of a title — and the welcome screen
  // for the invited parent can pre-fill these same fields.
  const [protagonistFirstName, setProtagonistFirstName] = useState('');
  const [protagonistLastName1, setProtagonistLastName1] = useState('');
  const [protagonistLastName2, setProtagonistLastName2] = useState('');
  const [selectedFamilyIds, setSelectedFamilyIds] = useState<string[]>([]);
  const [mode, setMode] = useState<RecordingMode>('presencial');
  const [parentEmail, setParentEmail] = useState('');
  const [frequency, setFrequency] = useState<NotificationFrequency>('daily');
  const [timeSlot, setTimeSlot] = useState<TimeSlot>('morning');
  const [channel, setChannel] = useState<NotificationChannel | null>(null);
  const [platform, setPlatform] = useState<InstallPlatform>('unknown');
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const {
    file: photo,
    preview: photoPreview,
    handleFileSelect: handlePhotoChange,
    clear: clearPhoto,
  } = useFilePreview();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const { createStory } = useStoryStore();
  const { familyGroups } = useFamilyGroupStore();
  const { inviteMember } = useFamilyInvitationStore();
  const { completeStep } = useOnboardingStore();
  const navigate = useNavigate();

  // Detect install platform once when the modal opens. The deferredPrompt
  // can change as the user navigates, but for a single modal session we
  // capture it once to avoid mid-flow surprises.
  useEffect(() => {
    if (isOpen) {
      setPlatform(detectInstallPlatform());
    }
  }, [isOpen]);

  // Auto-preselect the family when the user only has one. With multiple
  // families they have to choose explicitly so we don't bias the choice.
  useEffect(() => {
    if (isOpen && familyGroups.length === 1) {
      setSelectedFamilyIds([familyGroups[0].id]);
    }
  }, [isOpen, familyGroups]);

  const relationshipLabel = formData.relationship
    ? `mi ${formData.relationship.toLowerCase()}`
    : 'esa persona';
  // Same relationship phrased in second person, used in toasts/copy
  // that addresses the inviter ABOUT the recipient (e.g., "Le hemos
  // enviado una invitación a tu madre"). The 1st-person form above
  // stays for copy where the inviter is talking about themselves
  // ("Estoy con mi madre y voy a grabar yo").
  const relationshipLabel2nd = formData.relationship
    ? `tu ${formData.relationship.toLowerCase()}`
    : 'esa persona';

  // Derived title — auto-built from the protagonist's name fields. Falls
  // back to "La historia de…" while the user is still typing.
  const computedTitle = (() => {
    const fn = protagonistFirstName.trim();
    if (!fn) return '';
    const parts = [fn, protagonistLastName1.trim(), protagonistLastName2.trim()].filter(Boolean);
    return `La historia de ${parts.join(' ')}`;
  })();

  const titlePreview = computedTitle || 'La historia de…';

  const resetState = () => {
    setStep(1);
    setFormData({ relationship: '', dateOfBirth: '' });
    setProtagonistFirstName('');
    setProtagonistLastName1('');
    setProtagonistLastName2('');
    setSelectedFamilyIds([]);
    setMode('presencial');
    setParentEmail('');
    setFrequency('daily');
    setTimeSlot('morning');
    setChannel(null);
    setShowIOSInstructions(false);
    clearPhoto();
    setErrors({});
  };

  const handleClose = () => {
    onClose();
    resetState();
  };

  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!protagonistFirstName.trim()) {
      newErrors.protagonistFirstName = 'El nombre es requerido';
    }
    if (!protagonistLastName1.trim()) {
      newErrors.protagonistLastName1 = 'El primer apellido es requerido';
    }
    if (!formData.relationship) {
      newErrors.relationship = 'La relación es requerida';
    }
    const dateValidation = validateDateOfBirth(formData.dateOfBirth);
    if (!dateValidation.isValid) {
      newErrors.dateOfBirth = dateValidation.error!;
    }
    if (selectedFamilyIds.length === 0) {
      newErrors.families = 'Debes seleccionar al menos una familia';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (mode === 'distancia') {
      const trimmed = parentEmail.trim();
      if (!trimmed) {
        newErrors.parentEmail = 'Email requerido para la grabación a distancia';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        newErrors.parentEmail = 'Email no válido';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Steps 3-5 don't have field validation (they're all selections from a
  // closed set of options that always have a default), so advancing is
  // always allowed.
  const advance = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step === 5) return; // submit happens via form, not advance
    setErrors({});
    setStep((step + 1) as Step);
  };

  const goBack = () => {
    if (step === 1) return;
    setErrors({});
    setStep((step - 1) as Step);
  };

  // Request permission + subscribe inline on Android / mobile Chrome /
  // desktop Chrome. PRO-348 iter 11: no PWA install required — push
  // works in the browser tab as long as permission is granted.
  // Failure (denied permission or no subscription) is silent — the
  // user's stored channel stays as 'push' but the cron will fall back
  // to email if no subscription exists at delivery time. The desktop
  // user gets the install-instructions email on submit so they can
  // finish on their phone.
  const requestPushPermissionInline = async () => {
    if (!('Notification' in window)) return;
    try {
      const result = await Notification.requestPermission();
      if (result !== 'granted') return;

      const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
      if (!vapidKey) {
        console.warn('[CreateStoryModal] VITE_VAPID_PUBLIC_KEY not set; skipping push subscribe');
        return;
      }
      const subscription = await subscribeToPush(vapidKey);
      if (subscription) {
        await savePushSubscription(subscription);
      }
    } catch (err) {
      console.warn('[CreateStoryModal] Permission/subscribe failed:', err);
    }
  };

  const handleChannelPick = async (next: NotificationChannel) => {
    setChannel(next);
    setErrors((prev) => ({ ...prev, channel: '' }));
    if (next !== 'push') return;

    if (platform === 'ios' && !isStandalone()) {
      // iOS Safari outside the standalone PWA can't subscribe to push.
      // Show the Share → Add to Home Screen instructions overlay so the
      // user knows to install before push will actually deliver. We
      // still save the channel as 'push' — the rest of the flow trusts
      // that they'll follow the steps.
      setShowIOSInstructions(true);
      return;
    }

    if (platform === 'desktop' || platform === 'unknown') {
      // Desktop: nothing useful we can install / subscribe from here.
      // The install-instructions email goes out in handleSubmit and
      // points to /activate-push so the user can finish on their phone.
      // 'unknown' = mobile-ish browser without a deferred prompt; we
      // still attempt permission inline as a best effort.
      if (platform === 'unknown') {
        await requestPushPermissionInline();
      }
      return;
    }

    // Android / desktop Chrome with permission API: request inline.
    await requestPushPermissionInline();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep2()) return;

    if (channel === null) {
      setErrors({ channel: 'Elige cómo quieres recibir los avisos.' });
      return;
    }

    setLoading(true);

    try {
      const result = await createStory({
        ...formData,
        title: computedTitle || `La historia de ${protagonistFirstName.trim()}`,
        mode: 'adult',
        photo,
        familyGroupIds: selectedFamilyIds,
        protagonistFirstName: protagonistFirstName.trim(),
        protagonistLastName1: protagonistLastName1.trim(),
        protagonistLastName2: protagonistLastName2.trim() || undefined,
      });

      if (result.success) {
        completeStep(1);

        if (mode === 'distancia' && parentEmail.trim()) {
          const familyId = selectedFamilyIds[0];
          const inviteResult = await inviteMember(
            familyId,
            parentEmail.trim(),
            'editor',
            'protagonist',
            result.storyId!,
          );
          if (inviteResult.success) {
            toast.success(`Le hemos enviado una invitación a ${relationshipLabel2nd}.`);
          } else {
            toast.error(
              inviteResult.error ||
                'Historia creada, pero no pudimos enviar el enlace. Inténtalo de nuevo desde Familia.',
            );
          }
        }

        const prefResult = await upsertPreferences(result.storyId!, {
          frequency,
          channel,
          preferred_hour: slotToHour(timeSlot),
          // distancia mode: this row tracks the inviter's digest of new
          // content from the protagonist (not a reminder to record).
          // presencial mode: inviter and protagonist are the same person,
          // and the row drives reminders to record (default 'protagonist').
          role: mode === 'distancia' ? 'inviter' : 'protagonist',
        });
        if (!prefResult.success) {
          console.warn(
            '[CreateStoryModal] Notification preference upsert failed:',
            prefResult.error,
          );
        }

        // Desktop user picked "En el móvil": fire an email pointing to
        // /activate-push so they can activate the avisos from their
        // phone with one tap. PRO-348 iter 11: no PWA install required;
        // the page just requests permission + subscribes in the browser
        // tab. Failure is non-fatal — channel is already saved as
        // 'push' and reminder emails also carry the activate-push CTA.
        if (channel === 'push' && platform === 'desktop') {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            const functionUrl = `${supabase.supabaseUrl}/functions/v1/send-install-instructions`;
            const res = await fetch(functionUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session?.access_token}`,
              },
              body: JSON.stringify({ story_id: result.storyId }),
            });
            if (res.ok) {
              toast.success(
                'Te hemos enviado un correo. Ábrelo en tu móvil para activar los avisos.',
                { duration: 6000 },
              );
            }
          } catch (err) {
            console.warn(
              '[CreateStoryModal] send-install-instructions failed:',
              err,
            );
          }
        }

        onClose();
        navigate(`/story-recorder/${result.storyId}`);
        resetState();
      } else {
        setErrors({ submit: result.error || 'Error desconocido' });
      }
    } catch (error) {
      console.error('[CreateStoryModal] Exception during story creation:', error);
      setErrors({ submit: 'Error de conexión' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Nueva historia"
      maxWidth="md"
    >
      <p className="text-xs text-stone-500 -mt-4 mb-4">
        Paso {step} de 5 · {STEP_TITLES[step]}
      </p>

      {/* Step 1 — person details */}
      {step === 1 && (
        <div className="space-y-4">
          {/* Live title preview — updates letter-by-letter as the user
              types the protagonist's first name. Sets the tone that the
              user is already starting to build the story. */}
          <div className="rounded-xl bg-rose-50/40 border border-rose-100 px-4 py-3">
            <p className="text-xs uppercase tracking-[0.14em] text-rose-500 font-semibold">
              Título de la historia
            </p>
            <p
              className={`font-display text-lg leading-snug mt-1 ${
                computedTitle ? 'text-stone-900' : 'text-stone-400 italic'
              }`}
            >
              {titlePreview}
            </p>
          </div>

          <div className="flex items-start gap-4">
            <div className="shrink-0">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="relative group"
              >
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="w-20 h-20 rounded-full object-cover border-2 border-stone-200"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-rose-50 to-rose-100 border-2 border-stone-200 flex items-center justify-center">
                    <User className="h-8 w-8 text-rose-400" />
                  </div>
                )}
                <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-white border-2 border-stone-200 rounded-full flex items-center justify-center group-hover:bg-stone-50 transition-colors">
                  <Camera className="h-3.5 w-3.5 text-stone-500" />
                </div>
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <Input
                label="Nombre"
                value={protagonistFirstName}
                onChange={(e) => setProtagonistFirstName(e.target.value)}
                error={errors.protagonistFirstName}
                placeholder="Ej: Elena"
                autoComplete="given-name"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="Primer apellido"
              value={protagonistLastName1}
              onChange={(e) => setProtagonistLastName1(e.target.value)}
              error={errors.protagonistLastName1}
              placeholder="Ej: García"
              autoComplete="family-name"
              required
            />
            <Input
              label="Segundo apellido"
              value={protagonistLastName2}
              onChange={(e) => setProtagonistLastName2(e.target.value)}
              placeholder="(opcional)"
              autoComplete="additional-name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Relación *
            </label>
            <select
              value={formData.relationship}
              onChange={(e) =>
                setFormData({ ...formData, relationship: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 text-sm ${
                errors.relationship ? 'border-rose-500' : 'border-stone-300'
              }`}
              required
            >
              <option value="">Seleccionar relación</option>
              {RELATIONSHIPS.map((relation) => (
                <option key={relation} value={relation}>
                  {relation}
                </option>
              ))}
            </select>
            {errors.relationship && (
              <p className="mt-1 text-sm text-rose-600">{errors.relationship}</p>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="hidden"
            aria-label="Seleccionar imagen de galería"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handlePhotoChange}
            className="hidden"
            aria-label="Tomar foto con cámara"
          />

          <DatePicker
            label="Fecha de Nacimiento"
            value={formData.dateOfBirth}
            onChange={(date) =>
              setFormData({ ...formData, dateOfBirth: date })
            }
            error={errors.dateOfBirth}
            required
          />

          <FamilySelector
            availableFamilies={familyGroups}
            selectedFamilyIds={selectedFamilyIds}
            onChange={setSelectedFamilyIds}
            maxSelections={4}
            error={errors.families}
            mode="checkbox"
            label="Familias"
            required
          />
        </div>
      )}

      {/* Step 2 — recording mode */}
      {step === 2 && (
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-stone-700">
            ¿Cómo vas a grabar?
          </legend>
          <label
            className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
              mode === 'presencial'
                ? 'border-rose-500 bg-rose-50/40'
                : 'border-stone-200 hover:border-stone-300'
            }`}
          >
            <input
              type="radio"
              name="recordingMode"
              value="presencial"
              checked={mode === 'presencial'}
              onChange={() => setMode('presencial')}
              className="mt-0.5 h-4 w-4 accent-rose-500 shrink-0"
            />
            <span className="text-sm text-stone-800">
              <span className="font-medium">Presencial.</span>{' '}
              Estoy con {relationshipLabel} y voy a grabar yo.
            </span>
          </label>

          <label
            className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
              mode === 'distancia'
                ? 'border-rose-500 bg-rose-50/40'
                : 'border-stone-200 hover:border-stone-300'
            }`}
          >
            <input
              type="radio"
              name="recordingMode"
              value="distancia"
              checked={mode === 'distancia'}
              onChange={() => setMode('distancia')}
              className="mt-0.5 h-4 w-4 accent-rose-500 shrink-0"
            />
            <span className="text-sm text-stone-800">
              <span className="font-medium">A distancia.</span>{' '}
              Quiero que {relationshipLabel} pueda responder desde su móvil.
            </span>
          </label>

          {mode === 'distancia' && (
            <div className="pl-7">
              <Input
                label={`Email de ${relationshipLabel}`}
                type="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                error={errors.parentEmail}
                placeholder="email@ejemplo.com"
                inputMode="email"
                autoComplete="email"
              />
              <p className="mt-1.5 text-xs text-stone-500">
                Le enviaremos un enlace para que pueda responder cuando
                quiera. Caduca en 7 días.
              </p>
            </div>
          )}
        </fieldset>
      )}

      {/* Step 3 — frequency. In distancia mode this is the cadence at
          which the protagonist receives reminders to record the next
          topic (the inviter's progress digest inherits the same
          frequency, with one day of delay). */}
      {step === 3 && (
        <FrequencyPicker
          value={frequency}
          onChange={setFrequency}
          relationship={formData.relationship || undefined}
          legend={
            mode === 'distancia'
              ? `¿Cada cuánto le pediremos a ${
                  protagonistFirstName.trim() || 'tu familiar'
                } que conteste una pregunta?`
              : 'Te enviaremos recordatorios según el tiempo en el que quieras completar la historia.'
          }
        />
      )}

      {/* Step 4 — time of day */}
      {step === 4 && (
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-stone-700">
            {mode === 'distancia'
              ? `¿A qué hora le viene mejor a ${
                  protagonistFirstName.trim() || 'tu familiar'
                }?`
              : '¿A qué hora te viene mejor?'}
          </legend>
          <div className="flex flex-wrap gap-2" role="radiogroup">
            {TIME_SLOTS.map((slot) => {
              const selected = timeSlot === slot.value;
              return (
                <label
                  key={slot.value}
                  className={`flex-1 min-w-[110px] px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${
                    selected
                      ? 'border-rose-500 bg-rose-50/40'
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="timeSlot"
                    value={slot.value}
                    checked={selected}
                    onChange={() => setTimeSlot(slot.value)}
                    className="sr-only"
                  />
                  <div
                    className={`text-sm ${
                      selected
                        ? 'font-medium text-stone-900'
                        : 'text-stone-800'
                    }`}
                  >
                    {slot.label}
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    {slot.hourLabel}
                  </div>
                </label>
              );
            })}
          </div>
        </fieldset>
      )}

      {/* Step 5 — channel choice + install guide. In distancia mode this
          step is about the INVITER's progress updates, not the
          protagonist's reminders (the protagonist's channel is decided
          when they accept the invitation). In presencial mode the
          inviter is the protagonist, so it's both. */}
      {step === 5 && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'distancia' && (
            <p className="text-sm text-stone-600 leading-relaxed">
              Te avisaremos cuando{' '}
              {protagonistFirstName.trim() || 'tu familiar'} grabe algo
              nuevo, con la misma frecuencia que has elegido para sus
              recordatorios. Si no hay novedades, no te molestamos.
            </p>
          )}
          <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-stone-700">
              {mode === 'distancia'
                ? '¿Cómo quieres recibir las actualizaciones?'
                : '¿Cómo recibirás los avisos?'}
            </legend>
            <div className="space-y-3" role="radiogroup">
              {/* Buttons (not labels with radios) so onClick fires every
                  time, including when re-tapping the already-selected
                  card. Radio onChange wouldn't fire on re-tap, which is
                  what caused the first-tap-on-default-selected bug. */}
              <button
                type="button"
                onClick={() => handleChannelPick('push')}
                aria-pressed={channel === 'push'}
                className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border-2 transition-all ${
                  channel === 'push'
                    ? 'border-rose-500 bg-rose-50/40'
                    : 'border-stone-200 hover:border-stone-300'
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Smartphone size={16} className="text-stone-500" />
                    <span className="text-sm font-medium text-stone-900">
                      En tu móvil
                    </span>
                  </div>
                  <p className="text-xs text-stone-500 mt-0.5">
                    Llega al instante.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleChannelPick('email')}
                aria-pressed={channel === 'email'}
                className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border-2 transition-all ${
                  channel === 'email'
                    ? 'border-rose-500 bg-rose-50/40'
                    : 'border-stone-200 hover:border-stone-300'
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Mail size={16} className="text-stone-500" />
                    <span className="text-sm font-medium text-stone-900">
                      Por correo
                    </span>
                  </div>
                  <p className="text-xs text-stone-500 mt-0.5">
                    {mode === 'distancia'
                      ? 'Un día después de cada nueva grabación.'
                      : 'Llega cuando toque, en tu bandeja.'}
                  </p>
                </div>
              </button>
            </div>

            {/* Desktop user picked "En el móvil": tell them an email
                is coming with the activation link. They can't activate
                push from the device they're currently on; the email
                points to /activate-push and they finish on the phone. */}
            {channel === 'push' && platform === 'desktop' && (
              <p className="text-xs text-stone-600 leading-relaxed bg-stone-50 border border-stone-200 rounded-lg p-3">
                Estás en ordenador. Cuando crees la historia te enviaremos
                un correo con un enlace para activar los avisos desde tu
                móvil.
              </p>
            )}

            {/* iOS browser tab picked "En el móvil": same warning as the
                overlay we showed when they tapped the radio. Restates
                what they need to do. */}
            {channel === 'push' && platform === 'ios' && !isStandalone() && (
              <p className="text-xs text-stone-600 leading-relaxed bg-stone-50 border border-stone-200 rounded-lg p-3">
                En iPhone hay que añadir Ethernal a la pantalla de inicio
                antes de poder recibir avisos. Te lo explicamos en la
                ventana de pasos.
              </p>
            )}

            {errors.channel && (
              <p className="text-sm text-rose-600">{errors.channel}</p>
            )}
          </fieldset>

          {(errors.submit || errors.general) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              {errors.submit || errors.general}
            </div>
          )}

          <div className="flex space-x-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={goBack}
              fullWidth
              disabled={loading}
            >
              Atrás
            </Button>
            <Button type="submit" loading={loading} fullWidth>
              Crear historia
            </Button>
          </div>
        </form>
      )}

      {/* Footers for steps 1-4. Step 5 has its own form-submit footer
          above; the install instructions are shown as an overlay modal
          (rendered after the main Modal closes) rather than as a step. */}
      {step !== 5 && (
        <div className="flex space-x-3 pt-6">
          {step === 1 ? (
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              fullWidth
            >
              Cancelar
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={goBack}
              fullWidth
            >
              Atrás
            </Button>
          )}
          <Button type="button" onClick={advance} fullWidth>
            Continuar
          </Button>
        </div>
      )}
    </Modal>

    {/* iOS Add-to-Home-Screen overlay. Only fires on iOS Safari outside
        the standalone PWA (the one case where push genuinely requires an
        install). The user's channel is already saved as 'push'; this
        overlay just delivers the 3-step guidance. */}
    {isOpen && showIOSInstructions && (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6"
        onClick={() => setShowIOSInstructions(false)}
      >
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
        <div
          className="relative bg-white rounded-2xl px-6 py-6 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-stone-100"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="install-instructions-title"
        >
          <h3
            id="install-instructions-title"
            className="text-lg font-medium text-stone-900 mb-5"
          >
            Cómo añadir Ethernal a tu iPhone
          </h3>
          <PlatformInstallGuide platform="ios" />
          <div className="mt-6">
            <Button
              type="button"
              onClick={() => setShowIOSInstructions(false)}
              fullWidth
            >
              Hecho
            </Button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default CreateStoryModal;
